import React, { useEffect } from 'react';

import Link from '@/components/Link';

import { ExpertRow } from './ExpertRow';
import { safeNum } from './constants';
import type { DashboardER } from './useDashboardData';
import { useERCandidates } from './useERCandidates';

interface ProjectERListProps {
  expertRequests: DashboardER[];
  expanded?: boolean;
}

function ERSection({ er, expanded }: { er: DashboardER; expanded?: boolean }): React.ReactNode {
  const { fetchCandidates, candidates, loading, error } = useERCandidates();

  useEffect(() => {
    if (expanded) fetchCandidates(er.id);
  }, [er.id, expanded, fetchCandidates]);

  const stats = er.stats;
  const totalExperts =
    safeNum(stats?.suggested) +
    safeNum(stats?.vetting) +
    safeNum(stats?.verified) +
    safeNum(stats?.matched);
  const calls = safeNum(stats?.calls);
  const verifiedCount = safeNum(stats?.verified);

  return (
    <div className="first:mt-0 mt-10 rounded-sm border border-surface-border bg-surface-card px-16 py-14">
      <div className="mb-8 flex items-center justify-between">
        <Link
          to={er.html_url}
          className="text-14 font-semibold text-on-surface no-underline hover:underline"
        >
          {er.name}
        </Link>
      </div>

      <div className="mb-8 flex items-center gap-8 text-12 text-on-surface-muted">
        <span>{`${totalExperts} ${totalExperts === 1 ? 'expert' : 'experts'}`}</span>
        <span>&middot;</span>
        <span>{`${calls} ${calls === 1 ? 'call' : 'calls'}`}</span>
        {verifiedCount > 0 && (
          <>
            <span>&middot;</span>
            <span className="rounded bg-yellow-500/15 px-6 py-1 text-[1.1rem] font-semibold text-yellow-800 dark:text-yellow-300">
              {`Review ${verifiedCount} ${verifiedCount === 1 ? 'expert' : 'experts'}`}
            </span>
          </>
        )}
      </div>

      {error && candidates.length === 0 ? (
        <div className="py-4 text-12 text-on-surface-muted">
          Couldn&apos;t load experts for this request.
        </div>
      ) : loading && candidates.length === 0 ? (
        <div className="py-4 text-12 text-on-surface-subtle">Loading experts...</div>
      ) : (
        <div className="flex flex-col gap-6">
          {candidates.map((expert) => (
            <ExpertRow key={expert.id} expert={expert} expertRequestId={er.id} />
          ))}
        </div>
      )}
    </div>
  );
}

export function ProjectERList({ expertRequests, expanded }: ProjectERListProps): React.ReactNode {
  if (expertRequests.length === 0) {
    return <p className="py-8 text-12 text-on-surface-muted">No active expert requests.</p>;
  }

  return (
    <div className="flex flex-col">
      {expertRequests.map((er) => (
        <ERSection key={er.id} er={er} expanded={expanded} />
      ))}
    </div>
  );
}
